/**
 * The single source of truth for everything the server holds about the current
 * user: who they are, and the shell settings that follow them between machines.
 *
 * One repository rather than two because the two are one question in practice —
 * the settings belong *to* the session, and a change of user invalidates them. Its
 * job is the standard Android-guide data layer, moved into shared code: it owns
 * the in-memory cache, exposes state as flows, and offers suspend actions for
 * writes. Every view model reaches the server through here rather than through
 * [LunaPinApi] directly.
 *
 * That "one question" is why signing in and out live here too rather than on the
 * view model that offers the buttons. A sign-in replaces both halves at once, and it
 * has to replace them in the right order — see [adopt]. A view model that called the
 * API itself would leave this cache holding the previous account's tabs while
 * everything downstream believed the new account had arrived.
 *
 * @see se.soderbjorn.lunapin.clientserver.LunaPinApi
 */
package se.soderbjorn.lunapin.client

import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.clientserver.AccountSettings
import se.soderbjorn.lunapin.clientserver.AiTestResult
import se.soderbjorn.lunapin.clientserver.AiUsage
import se.soderbjorn.lunapin.clientserver.ApiFailure
import se.soderbjorn.lunapin.clientserver.Bookmark
import se.soderbjorn.lunapin.clientserver.CancelBackfillResult
import se.soderbjorn.lunapin.clientserver.EditBookmarkRequest
import se.soderbjorn.lunapin.clientserver.EnrichmentProgress
import se.soderbjorn.lunapin.clientserver.LunaPinApi
import se.soderbjorn.lunapin.clientserver.McpState
import se.soderbjorn.lunapin.clientserver.SessionState
import se.soderbjorn.lunapin.clientserver.SetAccountSettingsRequest
import se.soderbjorn.lunapin.clientserver.SummariseMissingResult
import se.soderbjorn.lunapin.clientserver.TagUse
import se.soderbjorn.lunapin.clientserver.TidyImpact
import se.soderbjorn.lunapin.clientserver.TidyPlan
import se.soderbjorn.lunapin.clientserver.TidyResult
import se.soderbjorn.lunapin.clientserver.TidySelection
import se.soderbjorn.lunapin.clientserver.TidyStatus
import se.soderbjorn.lunapin.clientserver.UiSettingKeys
import se.soderbjorn.lunapin.clientserver.UndoTidyResult

/**
 * Reads and writes the caller's session and shell settings.
 *
 * @param api the transport. Injected rather than constructed so a test can hand
 *   in an implementation that answers from memory — the architecture note's
 *   "shared singletons are lovely from Swift and hostile to tests" applies, and
 *   this is the seam that avoids it.
 */
class StorageRepository(
    private val api: LunaPinApi,
) {
    private val _sessionFlow = MutableStateFlow<SessionState?>(null)

    /**
     * Who the client is acting as, or `null` before the first load has answered.
     *
     * Null is "we do not know yet", distinct from a loaded [SessionState] whose
     * `user` is null, which is "nobody". Collapsing the two would make every
     * consumer render a signed-out screen for the first frame of every launch.
     */
    val sessionFlow = _sessionFlow.asStateFlow()

    private val _serverUnreachableFlow = MutableStateFlow(false)

    /**
     * Whether [start] failed to get any answer at all out of the server.
     *
     * A second flow rather than a field on [SessionState], because it is not a fact
     * about the session: nothing on the server said it, and [SessionState] is a wire
     * type whose every field arrived over that wire. What it describes is the state
     * of this client's one attempt to ask.
     *
     * It exists because the fallback [start] adopts on failure — `SessionState()` —
     * is indistinguishable from a real answer out of a server that was started with
     * no credentials, and the signed-out surface has to tell those two apart. Without
     * it, a 500, a timeout and a server that is not running all rendered the
     * nothing-configured card: an instruction to set two environment variables,
     * given to an operator who had already set them.
     *
     * **Only [start] ever sets this, and nothing clears it.** That is not an
     * oversight to tidy up later: nothing in the app retries the boot fetch, so the
     * only way out of the state is a reload, which is what the card the flag
     * produces tells the reader to do. A retry button is a reasonable thing to want
     * here and would be the thing to add — with a clear on success — rather than
     * clearing it from some unrelated call that happened to succeed.
     */
    val serverUnreachableFlow = _serverUnreachableFlow.asStateFlow()

    /** The stored settings, keyed by persistence key. Guarded by [mutex] throughout. */
    private val settings: MutableMap<String, String> = mutableMapOf()

    /**
     * Whether [settings] is what the server answered, or what a failure to ask leaves behind.
     *
     * Guarded by [mutex] with the cache it describes, and written on the same line as every
     * replacement of it, so the two cannot disagree.
     *
     * It exists because an empty cache has two causes and only one of them is a fact about the
     * user: they have never stored anything, or we could not find out. Every consumer so far
     * has been a *reader*, for which the two are the same — a missing font is the default font
     * either way. [settingsWereRead] is for the one consumer that writes.
     */
    private var settingsCameFromServer: Boolean = false

    private val _settingsRevisionFlow = MutableStateFlow(0)

    /**
     * Bumped every time the settings cache is replaced wholesale — by [start], by [refresh],
     * and by a sign-in or sign-out.
     *
     * A counter rather than the settings themselves, and that is the same choice LPN-27's
     * channel makes on the wire: **identity, not payload**. A consumer that hears "the cache
     * is new" goes back to [setting] and reads what it actually cares about; a consumer that
     * was handed a map would be a second way state arrives, and the two drift.
     *
     * It exists because [refresh] made the cache re-readable and nothing was watching it.
     * `WorkspaceBackingViewModel` restores on a change of *user*, correctly — the workspace of
     * the same account arriving again is not a new person — so a foreground refetch that
     * brought back a workspace edited on the laptop would have changed nothing on screen.
     * This is what such a consumer collects.
     *
     * **Not bumped by [putSetting].** A local write is this client's own change, already
     * applied wherever it came from; republishing it would be the echo LPN-27 spends a whole
     * `originId` on suppressing.
     */
    val settingsRevisionFlow = _settingsRevisionFlow.asStateFlow()

    /**
     * Completes when [start] has finished its first attempt — successfully or not.
     *
     * The load must be *awaited* rather than applied afterwards, because the shell
     * reads the persister while it mounts: having the answer first is the
     * difference between painting the user's own layout and painting an empty
     * default that flinches to their real one a moment later.
     */
    private val loaded = CompletableDeferred<Unit>()

    /** Serialises the cache replacement in [start] against concurrent [putSetting] calls. */
    private val mutex = Mutex()

    /**
     * Fetch the session and the caller's settings. Call once at boot, before the
     * shell mounts.
     *
     * Never throws. A server that cannot be reached leaves the cache empty and the
     * session at a signed-out [SessionState], which degrades the app to
     * "everything at its default, nothing remembered" rather than failing to boot
     * — the honest outcome, and the one a user can still look at.
     *
     * That fallback is a *fabrication*, though, and the fact that it is one is
     * published in [serverUnreachableFlow] rather than discarded. It is byte for
     * byte what a correctly-running server with no credentials configured answers,
     * so a surface reading only the session cannot tell "nobody has set this up" from
     * "nothing answered" — and it will confidently explain the first while the second
     * is what happened.
     */
    suspend fun start() {
        try {
            refresh()
        } finally {
            // In `finally` so a cancellation mid-load cannot leave every awaiting
            // reader blocked forever on a load that will never resume.
            loaded.complete(Unit)
        }
    }

    /**
     * Ask the server again: the session, and the settings that belong to it.
     *
     * [start]'s body, callable more than once. It exists because [start] completes
     * [loaded] and nothing re-runs it, so before this there was no way to ask a second
     * time without building a second repository — and a second repository is a second
     * cache, which is the thing this class exists to not have.
     *
     * Called when a phone returns to the foreground, because a backgrounded app hears
     * about nothing: no socket, and (until LPN-27) no channel at all. Also the way a
     * writer re-reads before writing a whole-blob setting, which is the staleness rule
     * LPN-24 carries.
     *
     * Never throws, like [start], and for the same reason — a refetch that failed and
     * threw would take out whichever intent happened to ask.
     *
     * **It republishes the session even when nothing changed.** That is deliberate: the
     * settings cache behind it may well have changed, and consumers watching the session
     * alone (`WorkspaceBackingViewModel` compares user ids) are correct to ignore it.
     * [settingsRevisionFlow] is what a consumer of the *cache* watches.
     */
    suspend fun refresh() {
        val fetched = runCatching { api.session() }.getOrNull()
        // Before [adopt], not after: adopt publishes the session, and a collector
        // woken by that is entitled to a flag that already describes the same
        // attempt. Set the other way round, the signed-out surface would paint
        // the nothing-configured card and correct itself a frame later.
        _serverUnreachableFlow.value = fetched == null
        adopt(fetched ?: SessionState())
    }

    // ── Signing in and out ───────────────────────────────────────────────────
    //
    // Here rather than on a view model, for the reason the file header gives: this is
    // the session's source of truth, and a view model that called `LunaPinApi`
    // directly would leave this cache describing whoever was signed in a moment ago.
    // Everything downstream keys off `sessionFlow` — the workspace reloads on a new
    // user id — so a sign-in that did not land here would be a sign-in nothing else
    // noticed.

    /**
     * Sign in as this deployment's one account, and adopt the session it produced.
     *
     * The only way in there is. Only offer it when [SessionState.isSignInConfigured] said
     * so: a server given neither variable answers 503, which is an answer for whoever
     * deployed it rather than for whoever is looking at a form.
     *
     * Neither argument is retained. This call is the last thing in the client that sees
     * the password — the session cookie the server sets on the response is the credential
     * from here on, so keeping the password in order to avoid re-reading a form field
     * would be storing a permanent secret to save a keystroke.
     *
     * @param username as typed, already trimmed by the caller.
     * @param password as typed, byte for byte. **Never logged, here or anywhere.**
     * @throws ApiFailure 401 for a wrong username *or* a wrong password — the two are
     *   indistinguishable by design, so nothing downstream may try to tell them apart —
     *   429 with the wait in its message once a caller has failed several times, and 503
     *   when this server has no credentials configured. `SignInAttempt` is where a
     *   failure becomes a sentence.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun signIn(username: String, password: String) {
        // `wantsSessionId` is left at its default of false, and spelling that out here would
        // be spelling out the absence of a request. **A browser must never be handed the
        // session id in the body**: it is holding it as an `HttpOnly` cookie, and a copy in a
        // field page script can read is exactly what that attribute exists to prevent. The
        // mobile path does not come through this repository at all — see
        // `SyncSetupBackingViewModel`, which has a device to store one on.
        adopt(api.signIn(username, password).session)
    }

    /**
     * End this device's session.
     *
     * The server call goes first and its failure is **not** swallowed: a sign-out that
     * did not reach the server leaves a cookie that will sign the same browser back in
     * on the next load, so reporting success would be a lie with a one-reload
     * half-life. The caller decides what to say about it.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun signOut() {
        api.signOut()
        // `isSignInConfigured` survives, because it is a fact about the server rather
        // than about whoever was just signed in — and re-fetching it would be a round
        // trip for an answer already in hand. Dropping it instead would be worse than
        // wasteful: the signed-out surface reads it to decide whether to offer a way back
        // in, so a sign-out would land on "sign-in is not configured here".
        adopt((_sessionFlow.value ?: SessionState()).copy(user = null))
    }

    /**
     * Take [session] as the current one, with the settings that belong to it.
     *
     * The single funnel for every change of who is signed in, which is what makes "the
     * cache and the session agree" one claim to check rather than four.
     */
    private suspend fun adopt(session: SessionState) {
        // Nobody has no settings, and asking for them anyway would earn the 401 the
        // settings routes are right to send an anonymous caller.
        val stored = if (session.user == null) {
            null
        } else {
            runCatching { api.uiSettings() }.getOrNull()
        }
        mutex.withLock {
            // Replaced, not merged: this can be a *different* person's settings
            // arriving over the last one's. A user who has never opened the theme
            // manager has no custom-themes key, and merging would leave them holding
            // the themes — and the tabs — of whoever came before.
            settings.clear()
            // Only the keys we are prepared to hand back. The server enforces the same
            // allowlist on the way in; this side does not trust that it did, because a
            // key that arrived here would go straight into the toolkit.
            stored?.settings?.filterKeys { it in UiSettingKeys.persisted }
                ?.let { settings.putAll(it) }
            // Null covers both of this method's ways of not having settings — nobody is signed
            // in, and the fetch failed — and neither of them is evidence about what this
            // account has stored. See [settingsWereRead].
            settingsCameFromServer = stored != null
        }
        // After the cache is whole and before the session is published, so a consumer woken
        // by either one finds the other already true.
        _settingsRevisionFlow.value = _settingsRevisionFlow.value + 1
        // Last, and the order is load-bearing: a collector woken by a new user id goes
        // straight to the cache, so the cache has to already be theirs. Flipping the
        // session first would hand the arriving account the previous one's tabs for
        // however long the fetch above took.
        _sessionFlow.value = session
    }

    // ── What the push channel invalidates ────────────────────────────────────
    //
    // LPN-27's client half. An event says *what* changed and never what it changed to, so
    // everything below is a way of saying "go and read" to whoever cares — never a way of
    // handing them a value. See `ServerEvent`, where the four reasons for that are argued;
    // the one that bites here is that a channel carrying rows would be a second way state
    // arrives, beside this class, and the two would drift.
    //
    // Nothing here filters echoes. `ServerEventStream` drops a client's own events before
    // they reach this far, in the one place that sees all of them.

    /**
     * Re-read the settings cache alone, without asking who is signed in.
     *
     * [refresh]'s narrower sibling, for the event that names a settings key: the session has
     * not changed, and a `GET /api/session` per window drag on another device would be a round
     * trip to learn something nobody asked.
     *
     * Bumps [settingsRevisionFlow] on success. **Silently does nothing on failure**, and does
     * not touch [serverUnreachableFlow]: that flag describes the *boot* attempt and is
     * documented as being set by nothing else, whereas this is one of many small fetches whose
     * failure costs a repaint that the next event or the next reconnect will produce anyway.
     */
    suspend fun refreshSettings() {
        val user = _sessionFlow.value?.user ?: return
        val stored = runCatching { api.uiSettings() }.getOrNull() ?: return
        mutex.withLock {
            settings.clear()
            settings.putAll(stored.settings.filterKeys { it in UiSettingKeys.persisted })
            // Unconditionally true: this method returns early on both of the paths that would
            // have made it false, leaving the cache — and therefore this flag — untouched.
            settingsCameFromServer = true
        }
        // Guarded against a settings payload for somebody else, which is reachable only if the
        // account changed between the read above and this line — a race with a sign-in. The
        // cache would then be the new account's while the session flow still says the old, and
        // publishing a revision would tell every consumer to read it as though it were theirs.
        if (_sessionFlow.value?.user?.id == user.id) {
            _settingsRevisionFlow.value = _settingsRevisionFlow.value + 1
        }
    }

    private val _bookmarksChangedFlow = MutableStateFlow(BookmarksChange())

    /**
     * The last thing the server said about the collection, and how many times it has said
     * anything.
     *
     * A revision *and* a set of ids, because two consumers want different halves: a list
     * re-reads on any change and ignores the ids, while an open editor compares its own
     * bookmark against them and ignores a change to somebody else's. Publishing only the ids
     * would leave a list unable to tell one change from the next identical one; publishing
     * only a counter would make every editor on screen re-read for every save anywhere.
     */
    val bookmarksChangedFlow = _bookmarksChangedFlow.asStateFlow()

    private val _accountSettingsRevisionFlow = MutableStateFlow(0)

    /** Bumped when the account's typed preferences changed somewhere else. */
    val accountSettingsRevisionFlow = _accountSettingsRevisionFlow.asStateFlow()

    /**
     * Somebody else changed the collection.
     *
     * @param ids what changed, or empty for "the collection changed and this is not the list
     *   of how" — the backfill's answer. **Empty means everything may have changed**, not
     *   nothing, so a consumer matching on ids must treat it as a match.
     */
    fun onBookmarksChanged(ids: List<Long>) {
        _bookmarksChangedFlow.value = BookmarksChange(
            revision = _bookmarksChangedFlow.value.revision + 1,
            ids = ids,
        )
    }

    /** Somebody else changed the account's typed preferences. */
    fun onAccountSettingsChanged() {
        _accountSettingsRevisionFlow.value = _accountSettingsRevisionFlow.value + 1
    }

    /**
     * What changed in the collection, and when.
     *
     * @property revision how many times the server has said anything about it. Monotonic, and
     *   the thing a list collects — two identical changes in a row are two facts.
     * @property ids which bookmarks, or empty for "all of them, or an unlisted many".
     */
    data class BookmarksChange(
        val revision: Int = 0,
        val ids: List<Long> = emptyList(),
    ) {
        /**
         * Whether [id] is covered by this change.
         *
         * True for an empty [ids], which is the answer that keeps an editor correct after a
         * backfill: "not listed" there means "not enumerated", not "not affected".
         */
        fun covers(id: Long): Boolean = ids.isEmpty() || id in ids
    }

    /**
     * The stored value for [key], or `null` if the user has never set it.
     *
     * Suspends until the initial [start] has answered, which is what lets the web
     * shell's `Persister` be a thin adapter over this with no readiness state of
     * its own.
     *
     * Reads under [mutex] rather than straight off the map. It used to be lock-free on
     * the grounds that the cache is replaced only once, before any read — which stopped
     * being true when [adopt] gained the sign-in callers: `clear()` followed by
     * `putAll()` is two steps, and a read landing between them would answer "never set"
     * for a value the user has certainly set. Uncontended, so every read after the
     * first is still immediate.
     *
     * @param key one of [UiSettingKeys.persisted].
     */
    @Throws(CancellationException::class)
    suspend fun setting(key: String): String? {
        loaded.await()
        return mutex.withLock { settings[key] }
    }

    /**
     * Whether the settings cache is the server's answer rather than the absence of one.
     *
     * **The permission to write a default.** [setting] answers null for a key the user has
     * never set *and* for every settings fetch that failed, which is the right collapse for a
     * reader and a dangerous one for a writer: a client that seeds a default and stores it
     * would, on the wrong null, overwrite what the account really holds with what a failed
     * request looked like. `WorkspaceBackingViewModel.restore` is that writer and this is what
     * it asks first.
     *
     * False is deliberately the answer for a signed-out caller too. Nobody's settings are not
     * an empty set of settings, and a default written then would be written against whoever
     * signs in next.
     *
     * Suspends until the initial [start] has answered, exactly as [setting] does — a caller
     * that read a key and then asks this must not be told about a different attempt than the
     * one that produced the value in its hand.
     */
    @Throws(CancellationException::class)
    suspend fun settingsWereRead(): Boolean {
        loaded.await()
        return mutex.withLock { settingsCameFromServer }
    }

    /**
     * Remember [value] for [key] — in the cache always, on the server when the key
     * is one we store.
     *
     * The local write happens first and unconditionally, so a failed round-trip
     * costs the user only persistence across a reload, not the change they just
     * made in this session.
     *
     * Failures are swallowed deliberately. This is called from the toolkit's paint
     * path — on every flick of the dark/light control, and after every window drag
     * — and the honest consequence of a failed write is that the arrangement does
     * not survive the reload. A dialog over a window position would be a worse
     * outcome than the one it reports.
     *
     * @param key one of [UiSettingKeys.persisted]; anything else is cached and not
     *   sent, since the server would refuse it with a 400.
     * @param value the blob, stored verbatim.
     */
    /**
     * Everything this account has saved, newest first.
     *
     * **Not cached, unlike the settings above,** and the difference is what the two
     * are. A setting is written by this client and read back by it, so a local copy
     * is the truth until this client changes it. The collection is written from
     * *outside* the app — a bookmarklet in another window, a phone, the server's own
     * enrichment worker — so a cache here would be a copy that is wrong whenever the
     * feature is being used, and stale in a way nothing would invalidate.
     *
     * Every caller therefore asks the server, and the surface showing the list
     * decides when to ask again.
     *
     * @throws ApiFailure 401 when nobody is signed in. Not swallowed into an empty
     *   list: a caller rendering "no bookmarks yet" for what is really "we could not
     *   ask" is the same class of lie the sign-in landing card was fixed for.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun bookmarks(): List<Bookmark> = api.bookmarks().bookmarks

    /**
     * The one bookmark with this id, or **null when this account has no such bookmark** —
     * which is what an edit window opened on a row somebody has since deleted must be
     * told.
     *
     * ── Why this is a request by id, having once read the collection ─────────
     *
     * It used to be `bookmarks().firstOrNull { it.id == id }`, on the grounds that the
     * collection is fetched whole by every list window anyway, so picking a row out of it
     * cost a request this client could already make and a route per row would be a second
     * way to read one table. That argument named the condition that would end it — *the day
     * the collection needs paging* — and a collection of a few thousand is that day: opening
     * one editor made the server run the whole-collection query, serialise every row, and
     * the browser parse every row, to fill in one form. It was tens of seconds of somebody
     * watching a spinner.
     *
     * So there is a `GET /api/bookmarks/{id}` now, answering from the same scoped query the
     * edit and delete routes use — which is what keeps its 404 from being a second opinion
     * about what "no such bookmark" means, the one real cost the old shape avoided.
     *
     * **The 404 becomes the null here, and only here.** [LunaPinApi.bookmark] throws it, in
     * step with the other two verbs on that route; this is the one place that reads it as
     * "this account has no such bookmark" rather than as a failure, which is exactly the
     * distinction an open editor turns into its *gone* state.
     *
     * Not cached, for [bookmarks]' reason — the collection is written from outside this
     * app, so a window reopened after a reload must ask rather than remember.
     *
     * @throws ApiFailure 401 when nobody is signed in. Distinct from the null above: "we
     *   could not ask" is not "it is not there", and the window says different things
     *   about the two.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun bookmark(id: Long): Bookmark? =
        try {
            api.bookmark(id)
        } catch (failure: ApiFailure) {
            if (failure.status == HttpStatusCode.NotFound) null else throw failure
        }

    /**
     * Change one bookmark, and answer what is now stored.
     *
     * **Every field of [edit] is authoritative, including the blank ones** — this is the
     * one call in the app that can clear a title, a note or the last tag, and a caller
     * that sends a half-filled request empties what it left out. `EditBookmarkRequest`
     * argues why that is the right shape for a form and the wrong one for a bookmarklet.
     *
     * The answer is the stored row rather than the request echoed back: the server
     * normalises tags, so what was sent and what is now held are not the same object.
     *
     * @throws ApiFailure 404 for a bookmark this account no longer has, which is the
     *   signal an open editor turns into its *gone* state rather than a failure — see
     *   `EditBookmarkBackingViewModel`. 400 with the server's own sentence for a URL it
     *   will not store or a link already saved under that URL.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun editBookmark(id: Long, edit: EditBookmarkRequest): Bookmark =
        api.editBookmark(id, edit)

    /**
     * Forget one bookmark.
     *
     * @throws ApiFailure 404 when this account has no such bookmark — including because
     *   somebody deleted it a moment ago somewhere else. Not swallowed; see
     *   [LunaPinApi.deleteBookmark].
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun deleteBookmark(id: Long) {
        api.deleteBookmark(id)
    }

    /**
     * Ask for this bookmark to be read and summarised again, and answer the row as it now
     * stands — queued, with any previous failure cleared.
     *
     * The edit window's *Regenerate* and a failed row's *Try again*. **It answers when the
     * bookmark is in the queue, not when the summary arrives**, so a caller holding a
     * window has to notice the arrival some other way; the list window's answer is a timer
     * that runs only while a visible row is waiting.
     *
     * Uncached like everything else about the collection, and here the reason is sharper
     * than usual: the whole point of the call is that the row will change again shortly,
     * without this client doing anything.
     *
     * @throws ApiFailure 503 when this deployment has no model provider key and 409 when
     *   this account has turned both AI switches off. Both carry a sentence naming which of
     *   the two, written to be shown as-is — that sentence is the reason these routes refuse
     *   rather than quietly queueing work nothing will run. 404 for a bookmark this account
     *   no longer has.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun summariseBookmark(id: Long): Bookmark = api.summariseBookmark(id)

    /**
     * Queue every bookmark that has never been tried, and answer how many that was.
     *
     * @throws ApiFailure the same 503 and 409 [summariseBookmark] answers.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun summariseMissing(): SummariseMissingResult = api.summariseMissing()

    /**
     * How far this account's enrichment queue has got.
     *
     * Uncached, and here the reason is the whole feature rather than a convention: the caller
     * is a panel polling every couple of seconds precisely *because* the answer changes on
     * its own, and a cached one would be a progress display that never progresses.
     *
     * @throws ApiFailure 401 when nobody is signed in. Not the 503 and 409
     *   [summariseMissing] answers — counting a queue is honest on a server with no key.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun enrichmentProgress(): EnrichmentProgress = api.enrichmentProgress()

    /**
     * Empty this account's enrichment queue, and answer how many bookmarks came out of it.
     *
     * Undone by pressing the backfill again, which is why no surface calling this should ask
     * *are you sure* — see [LunaPinApi.cancelBackfill].
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun cancelBackfill(): CancelBackfillResult = api.cancelBackfill()

    /**
     * How much this account has asked the model for this month, and how many bookmarks a
     * backfill would pick up right now.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun aiUsage(): AiUsage = api.aiUsage()

    /**
     * Ask the server to make one real call to the model provider, and answer what came back.
     *
     * The settings screen's *Test connection*, and the only call in this class that
     * deliberately costs the operator money. It answers the question
     * [accountSettings]' `aiAvailable` cannot: that boolean says a key is *set*, this says
     * whether the key that is set *works*.
     *
     * **A rejected key comes back as an [se.soderbjorn.lunapin.clientserver.AiTestResult]
     * with `ok = false`, not as a thrown failure** — the test ran, and its sentence is the
     * useful part. The throws below are the states where no call was made at all.
     *
     * @throws ApiFailure 503 when this deployment has no key, 429 with the wait in its
     *   message once it has been pressed several times in a few minutes, 401 when nobody is
     *   signed in. Every message is written to be shown as-is, and **none of them contains
     *   the key or any part of it** — see the server's `AiTestRoutes`, which enforces that
     *   against any provider rather than trusting one.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun testAi(): AiTestResult = api.testAi()

    /**
     * What this account has chosen about summaries, tags and saving.
     *
     * **Not cached**, for the collection's reason rather than the settings blob's: the one
     * field of it that is not this account's choice — `aiAvailable` — is a fact about the
     * *deployment*, and it changes when an operator sets a variable and restarts, which is
     * an event nothing in this client would hear about. A settings window reopened after
     * that has to ask.
     *
     * An account that has never chosen anything answers the defaults rather than failing.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun accountSettings(): AccountSettings = api.accountSettings()

    /**
     * Store all of them, and answer what is now held.
     *
     * The whole set in one call, matching the route: this is a form applied, so it either
     * landed or it did not, and a half-applied set is not reachable.
     *
     * **Read the answer rather than assuming the request took effect** — the server
     * normalises, so `Rust` comes back `#rust`.
     *
     * @throws ApiFailure 400 carrying the server's own sentence for a value it will not
     *   accept — a tag count outside 0–10, an unknown summary length or language. Those are
     *   written to be shown to a person as-is, and a caller that replaced one with a generic
     *   failure would be throwing away the only part that says what to change.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun setAccountSettings(settings: SetAccountSettingsRequest): AccountSettings =
        api.setAccountSettings(settings)

    /**
     * Every tag this account uses, with its count.
     *
     * What a `#tag` field draws its menu from. Uncached like the collection it is derived
     * from, and for the same reason: a bookmark saved from the popup in another window
     * changes it, and there is nothing here that would hear about that.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun tags(): List<TagUse> = api.tags()

    // ── Tidying the vocabulary ───────────────────────────────────────────────
    //
    // Four passthroughs and no cache, for the collection's reason and one sharper: every one
    // of these is a statement about rows that a bookmarklet in another window can change, and
    // three of the four are about a change that is *about to* happen. A remembered plan is a
    // plan describing a vocabulary that has moved.
    //
    // Nothing here holds "there is a plan open" either. That belongs to the surface showing
    // the dialog — one settings window at a time has one — and a repository holding it would
    // be a second window's dialog silently adopting the first's proposals.

    /**
     * How large this account's vocabulary is, and whether the last tidy can still be undone.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun tidyStatus(): TidyStatus = api.tidyStatus()

    /**
     * Work out what could be tidied. Changes nothing, and is the one call here that can spend
     * the operator's key.
     *
     * @throws ApiFailure 429 with the wait in its message once it has been pressed several
     *   times in a few minutes, and only where there is a key to spend. 401 when nobody is
     *   signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun planTidy(): TidyPlan = api.planTidy()

    /**
     * What this selection would cost, counted server-side against the live collection.
     *
     * @throws ApiFailure 400 with the server's own sentence for a selection that cannot be
     *   applied at all. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun previewTidy(selection: TidySelection): TidyImpact = api.previewTidy(selection)

    /**
     * Apply it. Destructive, reversible by [undoTidy], and **not** something to put a
     * confirmation in front of — see [LunaPinApi.applyTidy].
     *
     * @throws ApiFailure 400 with the server's own sentence for a chain or a contradiction.
     *   401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun applyTidy(selection: TidySelection): TidyResult = api.applyTidy(selection)

    /**
     * Put the tags back as they were before the last tidy. One step; a second press answers
     * zeroes rather than failing.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun undoTidy(): UndoTidyResult = api.undoTidy()

    // ── Agent access ─────────────────────────────────────────────────────────
    //
    // Three passthroughs and no cache, for the tidy calls' reason and a sharper one: the
    // switch is a security gate several server paths read on every request, and a remembered
    // copy of it would be a client claiming an agent may act as you after the server stopped
    // agreeing. The grants move on their own too — one appears whenever a consent page is
    // approved in another tab, and a refresh moves a "last used" every hour.
    //
    // There is deliberately no `connect` here and there must never be; see [LunaPinApi].

    /**
     * Whether agents may act as this account, the `/mcp` URL to paste into one, and what is
     * connected.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun mcpState(): McpState = api.mcpState()

    /**
     * Turn agent access on or off, answering the state that landed rather than the one asked
     * for.
     *
     * Off revokes nothing: both gates re-read the flag per request, so agents stop within one
     * request and work again the moment it goes back on. [revokeMcpConnection] is what
     * deletes.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun setMcpEnabled(isEnabled: Boolean): McpState = api.setMcpEnabled(isEnabled)

    /**
     * Cut one agent off. Its next call 401s; every other connection is untouched.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun revokeMcpConnection(clientId: String): McpState = api.revokeMcpConnection(clientId)

    @Throws(CancellationException::class)
    suspend fun putSetting(key: String, value: String) {
        mutex.withLock { settings[key] = value }
        if (key !in UiSettingKeys.persisted) return
        runCatching { api.setUiSetting(key, value) }
    }
}
